import axios, { AxiosResponse } from "axios";
import {
  IdeaListSortingType,
  IPagedResultDto,
  IIdeaDto,
  IdeaListViewMode,
  ISelectListEnumItemDto,
  IIdeaListFilterItemDto,
  IdeaListDataItemType,
  IIdeasRequestDto,
  IIdeaListGlobalResponseSettingsDto,
  IIdeaListSpaceResponseSettingsDto,
  IIdeaListSettingsDto,
  IIdeaListSettingsAllMetadataDto,
  IIdeaListSpaceSpecificSettingsDto,
  IIdeaListAllMetadataDto
} from "@/backendTypes/allTypes";

const BASE_URL: string = process.env.VUE_APP_CWX_DEV_SERVER
  ? process.env.VUE_APP_CWX_API_BASE_URL
  : "";

axios.defaults.withCredentials = process.env.VUE_APP_CWX_DEV_SERVER;

export async function getIdeas(params: {
  itemsPerPage: number;
  pageNumber: number;
  searchPhrase: string;
  sortBy: IdeaListSortingType | null;
  sortDesc: boolean;
  spaceIds: string[];
  regionIds: string[];
  departmentIds: string[];
  groupIds: string[];
  categoryIds: string[];
  ideaTypeIds: string[];
  stateIds: string[];
}) {
  const ideasRequest: IIdeasRequestDto = {
    itemsPerPage: params.itemsPerPage,
    pageNumber: params.pageNumber,
    searchPhrase: params.searchPhrase,
    sortBy: params.sortBy != null ? params.sortBy : undefined,
    sortDesc: params.sortDesc,
    spaceIds: params.spaceIds.join(","),
    regionIds: params.regionIds.join(","),
    departmentIds: params.departmentIds.join(","),
    groupIds: params.groupIds.join(","),
    categoryIds: params.categoryIds.join(","),
    ideaTypeIds: params.ideaTypeIds.join(","),
    stateIds: params.stateIds.join(",")
  };

  let result: AxiosResponse<IPagedResultDto<IIdeaDto>>;
  try {
    result = await axios.get<IPagedResultDto<IIdeaDto>>(
      `${BASE_URL}/api/v1/ideas`,
      {
        params: ideasRequest
      }
    );
  } catch {
    return {
      ideas: [],
      totalItemsCount: 0,
      pageNumber: 0
    };
  }

  var ideas = result.data.data;
  if (process.env.VUE_APP_CWX_DEV_SERVER) {
    // code to have absolute urls for images when developing with "yarn run serve"
    for (const idea of ideas) {
      idea.imageLink =
        process.env.VUE_APP_CWX_WEBSITE_BASE_URL + idea.imageLink;
    }
  }
  return {
    ideas: ideas,
    totalItemsCount: result.data.paging.totalItemsCount,
    pageNumber: result.data.paging.pageNumber
  };
}

/**
 * Normalized interface for global list setting and space specific setting.
 * With same shape of settings object for both kind of lists we can write
 * one code to handle both lists.
 */
export interface IdeaListSettings {
  listViewMode: IdeaListViewMode;
  sortings: ISelectListEnumItemDto<IdeaListSortingType>[];
  filters: IIdeaListFilterItemDto[];
  spaceSpecificSettings: {
    [spaceId: number]:
      | {
          dataItems: ISelectListEnumItemDto<IdeaListDataItemType>[];
        }
      | undefined;
  };
}

async function GetIdeaGlobalListSettings(): Promise<IdeaListSettings> {
  const url = `${BASE_URL}/api/v1/idea-list-global-settings`;
  const result = await axios.get<IIdeaListGlobalResponseSettingsDto>(url);

  const settingsDto = result.data;

  const fullDataItemsEntries = Object.entries(
    settingsDto.allSpaceSpecificSettings
  ).map(([spaceId, setting]) => {
    const fullDataItemsInfoForSpace = getFullDataItemsForSpace(
      setting,
      settingsDto.allMetadata
    );

    return [spaceId, { dataItems: fullDataItemsInfoForSpace }] as const;
  });

  let mapSettings = function(iter: any) {
    const obj = {};

    for (const pair of iter) {
      if (Object(pair) !== pair) {
        throw new TypeError("iterable for fromEntries should yield objects");
      }

      const { "0": key, "1": val } = pair;

      Object.defineProperty(obj, key, {
        configurable: true,
        enumerable: true,
        writable: true,
        value: val
      });
    }

    return obj;
  };

  const spaceSpecificSettings = mapSettings(fullDataItemsEntries);

  return {
    ...prepareBaseIdeaListSettingsProperites(settingsDto),
    spaceSpecificSettings
  };
}

async function GetIdeaSpaceListSettings(
  spaceId: number
): Promise<IdeaListSettings> {
  const url = `${BASE_URL}/api/v1/idea-list-space-settings/${spaceId}`;
  const result = await axios.get<IIdeaListSpaceResponseSettingsDto>(url);

  const settingsDto = result.data;

  const fullDataItemsInfoForSpace1 = getFullDataItemsForSpace(
    settingsDto.spaceSpecificSettings,
    settingsDto.allMetadata
  );

  const spaceSpecificSettings = {
    [spaceId]: { dataItems: fullDataItemsInfoForSpace1 }
  };

  return {
    ...prepareBaseIdeaListSettingsProperites(settingsDto),
    spaceSpecificSettings
  };
}

function getFullDataItemsForSpace(
  spaceSpecificSettings: IIdeaListSpaceSpecificSettingsDto,
  allMetadata: IIdeaListAllMetadataDto
) {
  return spaceSpecificSettings.dataItemTypes.map(diType => {
    return allMetadata.dataItems.find(di => di.value == diType)!;
  });
}

function prepareBaseIdeaListSettingsProperites(
  spaceOrGlobalListSetting: IIdeaListSettingsDto &
    IIdeaListSettingsAllMetadataDto
) {
  const sortings = spaceOrGlobalListSetting.sortingTypes.map(sortingType => {
    return spaceOrGlobalListSetting.allMetadata.sortings.find(
      s => s.value == sortingType
    )!;
  });

  const filters = spaceOrGlobalListSetting.filterTypes
    .map(filterType => {
      return spaceOrGlobalListSetting.allMetadata.filters.find(
        f => f.type == filterType
      )!;
    })
    //we are filtering filters that are not available in allMetadata list.
    //This can happen because for now we don't support all filters. Like "CreationDate" filter that has different UI (date range)
    .filter(f => f !== undefined);

  return {
    listViewMode: spaceOrGlobalListSetting.listViewMode,
    sortings,
    filters
  };
}

export async function getIdeaListSettings(
  spaceId: number | null
): Promise<IdeaListSettings> {
  if (typeof spaceId === "number") {
    return await GetIdeaSpaceListSettings(spaceId);
  } else {
    return await GetIdeaGlobalListSettings();
  }
}
